import { useInView } from "react-intersection-observer";

import Typography from "@/design-system/Typography";
import TelemetryButton from "@/design-system/telemetry-button";
import { polishCurrencyFormatter } from "@/lib/formatters";
import { type ITelemetryCustom, sendTelemetryEvent } from "@/lib/telemetry";
import { languageSelector } from "@/src/contexts";
import PriceComparisonItemCashbackLabel from "@/src/search/PriceComparison/PriceComparisonItemCashbackLabel";
import PriceComparisonItemPrice from "@/src/search/PriceComparison/PriceComparisonItemPrice";
import ImageSeo from "@Components/Image-seo";
import * as stylex from "@stylexjs/stylex";

import {
    ComposableSpacing,
    type PropsWithStyleX,
    colors,
    globalLimitStyles,
} from "../../../styles/tokens.stylex";
import ResourcesEn from "./comparison-offers-modal.resources.en.json";
import Resources from "./comparison-offers-modal.resources.json";

export interface PriceComparisonItemProps extends PropsWithStyleX {
    id: string;
    name: string | undefined;
    price: number;
    image: string | undefined;
    maxCashback?: number;
    slug: string | undefined;
    onClick?: () => void;
    inViewTelemetry?: ITelemetryCustom;
    clickTelemetry?: ITelemetryCustom;
}

export default function OfferTile(props: PriceComparisonItemProps) {
    const resources = languageSelector(Resources, ResourcesEn);
    const href = `/produkty/${props.slug},${props.id}`;
    const { ref } = useInView({
        initialInView: false,
        threshold: 0.1,
        triggerOnce: true,
        onChange: (inView) => {
            if (inView) sendTelemetryEvent(props.inViewTelemetry);
        },
    });

    const price = polishCurrencyFormatter(props.price);

    if (props?.clickTelemetry?.actionType) {
        delete props.clickTelemetry.actionType;
    }

    return (
        <TelemetryButton
            {...props.clickTelemetry}
            styleX={[styles.itemContainer, props.styleX]}
            href={href}
            onClick={props.onClick}
            isExternal
            size="None"
            as="a"
        >
            <div {...stylex.props(styles.imageContainer)} ref={ref}>
                <ImageSeo styleX={styles.image} src={props.image} alt={props.name} size="m" loading="lazy" />
            </div>
            <div {...stylex.props(styles.defaultPadding)}>
                <Typography
                    fontStyle="BodyS"
                    styleX={[styles.productName, globalLimitStyles.TextTwoLinesCap]}
                >
                    {props.name}
                </Typography>

                <PriceComparisonItemPrice
                    variant="searchResultsDetails"
                    amount={price}
                    isMobile={true}
                    styleX={styles.priceItem}
                />

                <PriceComparisonItemCashbackLabel
                    labelText={resources.cashback}
                    styleX={styles.cashbackText}
                />
            </div>
        </TelemetryButton>
    );
}

const styles = stylex.create({
    itemContainer: {
        padding: 0,
        borderColor: colors.b_Primary,
        borderRadius: ComposableSpacing.s2,
        borderStyle: "solid",
        borderWidth: "1px",
        backgroundColor: colors.bg_Primary,
        color: "inherit",
        display: "flex",
        flexDirection: "column",
        userSelect: "none",
        height: "100%",
    },

    imageContainer: {
        aspectRatio: "1.22 / 1",
        backgroundColor: colors.bg_Secondary,
        borderTopLeftRadius: ComposableSpacing.s2,
        borderTopRightRadius: ComposableSpacing.s2,
        height: "min-content",
        width: "100%",
    },

    image: {
        padding: ComposableSpacing.s4,
        mixBlendMode: "multiply",
        objectFit: "contain",
        height: "100%",
        width: "100%",
    },
    productName: {
        height: "3em",
    },

    priceItem: {
        color: colors.fg_Primary,
        fontSize: "0.875rem",
    },

    cashbackText: {
        fontSize: "0.75rem",
    },

    defaultPadding: {
        padding: `${ComposableSpacing.s1} ${ComposableSpacing.s2}`,
        gap: ComposableSpacing.s1,
        display: "grid",
        gridTemplateRows: "1fr auto auto",
    },
});
